
import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { toast } from 'sonner';
import { useCentralizedAuth } from '@/context/CentralizedAuthProvider';
import { useDatabase } from '@/context/DatabaseContext';
import { AssessmentResult } from '@/types/assessment';
import LoadingScreen from '@/components/assessment/LoadingScreen';
import ActionCards from '@/components/results/ActionCards';
import ReportSummary from '@/components/results/ReportSummary';
import SupportSection from '@/components/results/SupportSection';
import NoResults from '@/components/results/NoResults';
import { getOpenAIKey } from '@/utils/apiKeyUtils';
import ConnectionWarning from '@/components/ConnectionWarning';
import { supabase } from '@/integrations/supabase/client';

const Results = () => {
  const { user } = useCentralizedAuth();
  const { getResultsForUser, isConnected } = useDatabase();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order');
  
  const [result, setResult] = useState<AssessmentResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [localResultFound, setLocalResultFound] = useState(false);
  const [activeUserId, setActiveUserId] = useState<string | null>(null);
  const [packageType, setPackageType] = useState<string | null>(null);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (data.session) {
          setActiveUserId(data.session.user.id);
          console.log('User authenticated with ID:', data.session.user.id);
        } else {
          console.log('No active session found');
          setActiveUserId('anonymous');
        }
      } catch (e) {
        console.error('Error checking session:', e);
        setActiveUserId('anonymous');
      }
    };
    
    const fetchResults = async () => {
      try {
        await checkSession();
        
        // Try to get user ID from multiple sources
        const userId = activeUserId || user?.id || 'anonymous';
        console.log('Checking for results with user ID:', userId);
        
        // If we have an order ID, try to get the package type
        if (orderId) {
          try {
            const { data: orderData, error: orderError } = await supabase
              .from('orders')
              .select('package_type')
              .eq('id', orderId)
              .maybeSingle();
              
            if (orderData && !orderError) {
              setPackageType(orderData.package_type);
              console.log('Package type:', orderData.package_type);
            }
          } catch (err) {
            console.error('Error fetching order details:', err);
          }
        }
        
        // If we have an order ID, try to get result for this specific order
        if (orderId) {
          console.log('Looking for results for order:', orderId);
          try {
            const { data, error } = await supabase
              .from('assessment_results')
              .select('*')
              .eq('order_id', orderId)
              .eq('user_id', userId)
              .maybeSingle();
              
            if (data && !error) {
              console.log('Found result for order:', data);
              setResult({
                id: data.id,
                userId: data.user_id,
                report: data.report,
                pdfUrl: data.pdf_url || '',
                createdAt: new Date(data.created_at),
                orderId: data.order_id
              });
              setLoading(false);
              return;
            }
          } catch (err) {
            console.error('Error fetching order-specific result:', err);
          }
        }
        
        // Check localStorage for a cached report
        const cachedReport = localStorage.getItem(`report_${userId}`);
        if (cachedReport) {
          try {
            const parsedReport = JSON.parse(cachedReport);
            console.log('Found cached report in localStorage:', parsedReport);
            setResult(parsedReport);
            setLocalResultFound(true);
            setLoading(false);
            return;
          } catch (e) {
            console.error('Error parsing cached report:', e);
          }
        }
        
        // Try to get results from database if user is authenticated
        if (userId !== 'anonymous') {
          console.log('Fetching results from database for user:', userId);
          try {
            const userResults = await getResultsForUser(userId);
            if (userResults && userResults.length > 0) {
              console.log('Found results in database:', userResults);
              setResult(userResults[0]);
              setLoading(false);
              return;
            }
          } catch (dbError) {
            console.error('Error fetching results from database:', dbError);
          }
        }
        
        // If no results found yet, check for responses in localStorage
        const localResponsesKey = `responses_${userId}`;
        const localResponses = localStorage.getItem(localResponsesKey);
        
        if (localResponses) {
          console.log('Found responses in localStorage');
          try {
            // Create a basic result from localStorage
            const createdAt = new Date();
            const basicReport = "# Basic Assessment Report\n\nThis is a simplified report based on your assessment. For a full report, please ensure you are logged in and have added your OpenAI API key in settings.";
            
            setResult({
              id: `local-${Date.now()}`,
              userId: userId,
              report: basicReport,
              pdfUrl: '',
              createdAt: createdAt
            });
            
            setLocalResultFound(true);
            toast.info('Using locally stored assessment data. For permanent storage, please make sure you are logged in.');
          } catch (e) {
            console.error('Error parsing localStorage data:', e);
            setError("Error loading locally stored results");
          }
        } else {
          console.log('No results found locally');
          setError("No assessment results found. Please complete an assessment first.");
        }
      } catch (error) {
        console.error('Error in fetchResults:', error);
        setError("Error loading results. Please try again later.");
        toast.error("There was an error loading your results. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    
    // Load OpenAI key if available
    const loadOpenAIKey = async () => {
      try {
        const key = await getOpenAIKey();
        console.log('OpenAI key loaded:', key ? 'Key found' : 'No key found');
      } catch (error) {
        console.error('Error loading OpenAI key:', error);
      }
    };
    
    fetchResults();
    loadOpenAIKey();
  }, [user, getResultsForUser, orderId]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <LoadingScreen />
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      
      <main className="flex-grow p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Assessment Results</h1>
            <p className="text-gray-600">
              {error ? error : result ? `Completed on ${new Date(result.createdAt).toLocaleDateString()}` : 'No results found'}
            </p>
            {localResultFound && (
              <div className="mt-2 text-amber-600 text-sm">
                <p>Note: These results are stored locally and not linked to your account.</p>
              </div>
            )}
          </div>
          
          {/* Show connection warning/status */}
          <ConnectionWarning />
          
          {error && !result ? (
            <NoResults errorMessage={error} />
          ) : (
            <>
              <ActionCards result={result} userEmail={user?.email} packageType={packageType} />
              <ReportSummary report={result?.report} />
              {/* Only show support section for gold package */}
              {packageType !== 'silver' && <SupportSection />}
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Results;
